import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import logo from '../../assets/logo.png';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: var(--primary);
  z-index: 1000;
  transition: all 0.3s ease;
  
  &.scrolled {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-sm) var(--spacing-md);
  max-width: var(--container-width);
  margin: 0 auto;
  height: 70px;
`;

const Logo = styled.div`
  padding: var(--spacing-xs);
  
  img {
    height: 40px;
    display: block;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileNav = styled.nav<{ $isOpen: boolean }>`
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--primary);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transform: ${({ $isOpen }) => $isOpen ? 'translateY(0)' : 'translateY(-100%)'};
  opacity: ${({ $isOpen }) => $isOpen ? '1' : '0'};
  visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  z-index: 999;
`;

const NavLink = styled.a`
  margin: 0 var(--spacing-md);
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 1px;
  font-size: 0.875rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -4px;
    left: 0;
    background-color: var(--accent-2);
    transition: width 0.3s ease;
  }
  
  &:hover:after {
    width: 100%;
  }
`;

const MobileNavLink = styled(NavLink)`
  margin: var(--spacing-md) 0;
  font-size: 1.25rem;
`;

const PhoneNumber = styled.a`
  margin-left: var(--spacing-lg);
  display: flex;
  align-items: center;
  font-weight: 700;
  
  &:before {
    content: '📞';
    margin-right: var(--spacing-xs);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  width: 30px;
  height: 20px;
  position: relative;
  z-index: 1000;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const HamburgerLine = styled.span<{ $isOpen: boolean }>`
  display: block;
  width: 30px;
  height: 2px;
  background-color: var(--secondary);
  position: absolute;
  left: 0;
  transition: all 0.3s ease;
  
  &:nth-child(1) {
    top: ${({ $isOpen }) => $isOpen ? '9px' : '0'};
    transform: ${({ $isOpen }) => $isOpen ? 'rotate(45deg)' : 'rotate(0)'};
  }
  
  &:nth-child(2) {
    top: 9px;
    opacity: ${({ $isOpen }) => $isOpen ? '0' : '1'};
  }
  
  &:nth-child(3) {
    top: ${({ $isOpen }) => $isOpen ? '9px' : '18px'};
    transform: ${({ $isOpen }) => $isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
  }
`;

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    document.body.style.overflow = !isOpen ? 'hidden' : '';
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <HeaderContainer className={scrolled ? 'scrolled' : ''}>
      <HeaderContent>
        <Logo>
          <img src={logo} alt="GS Autohaus Logo" />
        </Logo>

        <Nav>
          <NavLink href="#home">Home</NavLink>
          <NavLink href="#services">Services</NavLink>
          <NavLink href="#about">About Us</NavLink>
          <NavLink href="#contact">Contact</NavLink>
          <PhoneNumber href="tel:4158826075">(415) 882-6075</PhoneNumber>
        </Nav>

        <HamburgerButton onClick={toggleMenu}>
          <HamburgerLine $isOpen={isOpen} />
          <HamburgerLine $isOpen={isOpen} />
          <HamburgerLine $isOpen={isOpen} />
        </HamburgerButton>

        <MobileNav $isOpen={isOpen}>
          <MobileNavLink href="#home" onClick={toggleMenu}>Home</MobileNavLink>
          <MobileNavLink href="#services" onClick={toggleMenu}>Services</MobileNavLink>
          <MobileNavLink href="#about" onClick={toggleMenu}>About Us</MobileNavLink>
          <MobileNavLink href="#contact" onClick={toggleMenu}>Contact</MobileNavLink>
          <MobileNavLink href="tel:4158826075" onClick={toggleMenu}>(415) 882-6075</MobileNavLink>
        </MobileNav>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header; 